import {
  DESCRIPCION_ROL,
  DESCRIPCION_ROL_EN,
  LARGO_MINIMO_CONTRASENA,
  type PreparacionDeDosPasos,
  type Tema,
} from '@cnc/contracts';
import {useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {
  useActivarDosPasos,
  useCambiarContrasena,
  useDesactivarDosPasos,
  useGuardarPreferencias,
  usePrepararDosPasos,
} from '../api/sesion';
import {AYUDA_CUENTA as A} from '../componentes/ayuda-general';
import {MINUTOS_PARA_DESCANSO} from '../componentes/descansa-pantalla';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {SelectorDeIdioma} from '../componentes/selector-de-idioma';
import {useUsuario} from '../componentes/sesion-contexto';
import {SelectorDeTema} from '../componentes/tema';
import {et, tr} from '../i18n/idioma';
import {fechaHora} from '../utilidades/formato';

const clase = 'mt-1 w-full rounded-md border border-linea px-3 py-2 text-sm text-tinta';
const boton = 'rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';

// Mi cuenta: quien soy, cambiar la contrasena y la verificacion en dos pasos.
// Las rutas /cuenta/contrasena y /cuenta/dos-pasos abren la seccion que toca
// cuando el ingreso exige ese paso.
export function PaginaCuenta() {
  const usuario = useUsuario();
  const {pathname} = useLocation();
  const obligatoriaContrasena = usuario.debeCambiarContrasena;
  const obligatoriosDosPasos = usuario.debeConfigurarDosPasos && !obligatoriaContrasena;
  return (
    <section className="max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Mi cuenta', 'My account')}</h1>
        <p className="text-sm text-apagado">
          {usuario.nombre} · {usuario.correo} · {usuario.rolEtiqueta}
          <NotaDeAyuda texto={A.quienSoy} />
        </p>
        <p className="mt-1 text-xs text-apagado">{et(DESCRIPCION_ROL, DESCRIPCION_ROL_EN)[usuario.rol]}</p>
      </div>

      {obligatoriaContrasena && (
        <div className="rounded-lg border border-dorado/50 bg-dorado-claro p-4 text-sm">
          <div className="font-medium">
            {tr(
              'Antes de empezar, cambia tu contraseña temporal.',
              'Before you start, change your temporary password.',
            )}
          </div>
          <div className="mt-1 text-apagado">
            {tr(
              'La contraseña con la que entraste es de un solo uso. Elige una que solo tú conozcas.',
              'The password you signed in with is single-use. Choose one that only you know.',
            )}
          </div>
        </div>
      )}
      {obligatoriosDosPasos && (
        <div className="rounded-lg border border-dorado/50 bg-dorado-claro p-4 text-sm">
          <div className="font-medium">
            {tr(
              'Como administrador debes activar la verificación en dos pasos.',
              'As an administrator you must turn on two-step verification.',
            )}
          </div>
          <div className="mt-1 text-apagado">
            {tr(
              'Es un código que cambia cada 30 segundos en una app de tu teléfono. Protege la cuenta aunque alguien conozca tu contraseña.',
              'It is a code that changes every 30 seconds in an app on your phone. It protects the account even if someone knows your password.',
            )}
          </div>
        </div>
      )}

      {(pathname !== '/cuenta/dos-pasos' || obligatoriaContrasena) && (
        <CambioDeContrasena obligatoria={obligatoriaContrasena} />
      )}
      {!obligatoriaContrasena && <DosPasos obligatorios={obligatoriosDosPasos} />}
      <Idioma />
      <Apariencia />
      <AvisosYDescanso />
    </section>
  );
}

function AvisosYDescanso() {
  const usuario = useUsuario();
  const guardar = useGuardarPreferencias();
  // Cambia en pantalla de inmediato y se guarda en el perfil por detras.
  const [marcadas, setMarcadas] = useState({
    avisosDeLogros: usuario.preferencias.avisosDeLogros !== false,
    descansaPantalla: usuario.preferencias.descansaPantalla !== false,
  });
  const opciones = [
    {
      clave: 'avisosDeLogros' as const,
      titulo: tr('Avisos de logros', 'Achievement notices'),
      texto: tr(
        'Un aviso cuando un exportador llega a una exportación o un Big Bag redondo (la 100, el 9.000…).',
        'A notice when an exporter reaches a round export or Big Bag number (the 100th, the 9,000th…).',
      ),
    },
    {
      clave: 'descansaPantalla' as const,
      titulo: tr('Descansa pantalla', 'Screensaver'),
      texto: tr(
        `Tras ${MINUTOS_PARA_DESCANSO} minutos sin usar la aplicación aparece un barco que se llena de contenedores. Cualquier movimiento lo quita.`,
        `After ${MINUTOS_PARA_DESCANSO} minutes without using the application, a ship being loaded with containers appears. Any movement removes it.`,
      ),
    },
  ];
  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">{tr('Avisos y descansa pantalla', 'Notices and screensaver')}</h2>
        {/* Los logros salieron del menu (6/10/2026): su historial se abre desde aqui. */}
        <Link to="/logros" className="text-sm text-verde hover:underline">
          {tr('🏆 Ver logros', '🏆 See achievements')}
        </Link>
      </div>
      <div className="mt-2 space-y-3">
        {opciones.map(o => (
          <label key={o.clave} className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={marcadas[o.clave]}
              onChange={e => {
                const valor = e.target.checked;
                setMarcadas(m => ({...m, [o.clave]: valor}));
                void guardar
                  .mutateAsync({columnas: {}, [o.clave]: valor})
                  .catch(() => setMarcadas(m => ({...m, [o.clave]: !valor})));
              }}
            />
            <span>
              <span className="font-medium">
                {o.titulo}
                <NotaDeAyuda texto={A[o.clave]} dentroDeEtiqueta />
              </span>
              <span className="block text-xs text-apagado">{o.texto}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Tema claro u oscuro (decision 0023): queda en el perfil y se aplica en cualquier computador.
function Apariencia() {
  const usuario = useUsuario();
  const [tema, setTema] = useState<Tema>(usuario.preferencias.tema ?? 'claro');
  return (
    <div className="rounded-lg border border-linea bg-white p-5" data-prueba="apariencia">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">
          {tr('Apariencia', 'Appearance')}
          <NotaDeAyuda texto={A.tema} />
        </h2>
        <SelectorDeTema tema={tema} onCambiar={setTema} />
      </div>
      <p className="mt-1 text-xs text-apagado">
        {tr(
          'El modo oscuro descansa la vista con poca luz. "Como el sistema" sigue la configuración de tu computador. También se cambia con el botón ☀/☾ al pie del menú.',
          'Dark mode is easier on the eyes in low light. "Same as system" follows your computer setting. It can also be switched with the ☀/☾ button at the bottom of the menu.',
        )}
      </p>
    </div>
  );
}

function Idioma() {
  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">
          Idioma / Language
          <NotaDeAyuda texto={A.idioma} />
        </h2>
        <SelectorDeIdioma guardarEnPerfil />
      </div>
      <p className="mt-1 text-xs text-apagado">
        {tr(
          'El idioma queda guardado en tu perfil y se aplica en cualquier computador donde entres.',
          'The language is saved in your profile and applies on any computer you sign in from.',
        )}
      </p>
    </div>
  );
}

function CambioDeContrasena({obligatoria}: {obligatoria: boolean}) {
  const usuario = useUsuario();
  const cambiar = useCambiarContrasena();
  const navegar = useNavigate();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [hecho, setHecho] = useState(false);

  const enviar = async () => {
    setMensaje(null);
    setHecho(false);
    if (nueva !== repetida) {
      setMensaje(
        tr('La contraseña nueva y su repetición no coinciden.', 'The new password and its confirmation do not match.'),
      );
      return;
    }
    try {
      const resultado = await cambiar.mutateAsync({actual, nueva});
      setActual('');
      setNueva('');
      setRepetida('');
      setHecho(true);
      if (obligatoria) void navegar(resultado.debeConfigurarDosPasos ? '/cuenta/dos-pasos' : '/', {replace: true});
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? error.message
          : tr('No se pudo cambiar la contraseña.', 'Could not change the password.'),
      );
    }
  };

  return (
    <form
      className="rounded-lg border border-linea bg-white p-5"
      onSubmit={e => {
        e.preventDefault();
        void enviar();
      }}
    >
      <h2 className="font-medium">{tr('Contraseña', 'Password')}</h2>
      <p className="mt-1 text-xs text-apagado">
        {tr(
          `Al menos ${LARGO_MINIMO_CONTRASENA} caracteres, con letras y números. Al cambiarla se cierran tus otras sesiones.`,
          `At least ${LARGO_MINIMO_CONTRASENA} characters, with letters and numbers. Changing it signs out your other sessions.`,
        )}
      </p>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <label className="block text-xs text-apagado">
          {obligatoria ? tr('Contraseña temporal', 'Temporary password') : tr('Contraseña actual', 'Current password')}
          <NotaDeAyuda texto={A.actual} dentroDeEtiqueta />
          <input
            type="password"
            value={actual}
            onChange={e => setActual(e.target.value)}
            autoComplete="current-password"
            required
            className={clase}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Contraseña nueva', 'New password')}
          <NotaDeAyuda texto={A.nueva} dentroDeEtiqueta />
          <input
            type="password"
            value={nueva}
            onChange={e => setNueva(e.target.value)}
            autoComplete="new-password"
            required
            minLength={LARGO_MINIMO_CONTRASENA}
            className={clase}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Repite la nueva', 'Repeat the new one')}
          <NotaDeAyuda texto={A.repetida} dentroDeEtiqueta />
          <input
            type="password"
            value={repetida}
            onChange={e => setRepetida(e.target.value)}
            autoComplete="new-password"
            required
            className={clase}
          />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={cambiar.isPending} className={boton}>
          {cambiar.isPending ? tr('Guardando…', 'Saving…') : tr('Cambiar contraseña', 'Change password')}
        </button>
        {mensaje && <span className="text-sm text-alerta">{mensaje}</span>}
        {hecho && <span className="text-sm text-verde-oscuro">{tr('Contraseña cambiada.', 'Password changed.')}</span>}
        {!obligatoria && usuario.ultimoIngreso && (
          <span className="ml-auto text-xs text-apagado">
            {tr('Último ingreso:', 'Last sign-in:')} {fechaHora(usuario.ultimoIngreso)}
            <NotaDeAyuda texto={A.ultimoIngreso} />
          </span>
        )}
      </div>
    </form>
  );
}

function DosPasos({obligatorios}: {obligatorios: boolean}) {
  const usuario = useUsuario();
  const preparar = usePrepararDosPasos();
  const activar = useActivarDosPasos();
  const desactivar = useDesactivarDosPasos();
  const navegar = useNavigate();
  const [preparacion, setPreparacion] = useState<PreparacionDeDosPasos | null>(null);
  const [codigo, setCodigo] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);

  const empezar = async () => {
    setMensaje(null);
    try {
      setPreparacion(await preparar.mutateAsync());
    } catch (error) {
      setMensaje(error instanceof ErrorDeApi ? error.message : tr('No se pudo preparar.', 'Could not prepare.'));
    }
  };
  const confirmar = async () => {
    setMensaje(null);
    try {
      await activar.mutateAsync(codigo.trim());
      setPreparacion(null);
      setCodigo('');
      if (obligatorios) void navegar('/', {replace: true});
    } catch (error) {
      setMensaje(error instanceof ErrorDeApi ? error.message : tr('No se pudo activar.', 'Could not turn it on.'));
    }
  };
  const quitar = async () => {
    setMensaje(null);
    try {
      await desactivar.mutateAsync(codigo.trim());
      setCodigo('');
    } catch (error) {
      setMensaje(error instanceof ErrorDeApi ? error.message : tr('No se pudo desactivar.', 'Could not turn it off.'));
    }
  };

  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">{tr('Verificación en dos pasos', 'Two-step verification')}</h2>
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${usuario.dosPasosActivo ? 'bg-verde-claro text-verde-oscuro' : 'bg-papel text-apagado'}`}
        >
          {usuario.dosPasosActivo ? tr('Activa', 'On') : tr('No activa', 'Off')}
          <NotaDeAyuda texto={A.dosPasos} />
        </span>
      </div>
      <p className="mt-1 text-xs text-apagado">
        {tr(
          'Al entrar, además de la contraseña se pide un código de 6 dígitos de una app de autenticación en tu teléfono (Microsoft Authenticator, Google Authenticator, Authy…).',
          'When signing in, besides the password you are asked for a 6-digit code from an authenticator app on your phone (Microsoft Authenticator, Google Authenticator, Authy…).',
        )}
      </p>

      {!usuario.dosPasosActivo && !preparacion && (
        <button type="button" onClick={() => void empezar()} disabled={preparar.isPending} className={`mt-3 ${boton}`}>
          {preparar.isPending
            ? tr('Preparando…', 'Preparing…')
            : tr('Activar la verificación en dos pasos', 'Turn on two-step verification')}
        </button>
      )}

      {preparacion && (
        <div className="mt-3 grid gap-4 md:grid-cols-[auto_1fr]">
          <div
            className="w-56 rounded-md border border-linea bg-white p-2"
            aria-label={tr('Código QR para la app de autenticación', 'QR code for the authenticator app')}
            dangerouslySetInnerHTML={{__html: preparacion.qrSvg}}
          />
          <div className="text-sm">
            <ol className="list-decimal space-y-1 pl-5">
              <li>
                {tr(
                  'Abre la app de autenticación en tu teléfono y elige agregar una cuenta.',
                  'Open the authenticator app on your phone and choose to add an account.',
                )}
              </li>
              <li>
                {tr(
                  'Escanea este código QR. Si no puedes, escribe la clave a mano:',
                  'Scan this QR code. If you cannot, type the key by hand:',
                )}
              </li>
            </ol>
            <code className="mt-1 block break-all rounded bg-papel px-2 py-1 text-xs">{preparacion.secreto}</code>
            <ol className="mt-2 list-decimal space-y-1 pl-5" start={3}>
              <li>
                {tr(
                  'Escribe aquí el código de 6 dígitos que muestra la app y confirma.',
                  'Type here the 6-digit code shown by the app and confirm.',
                )}
              </li>
            </ol>
            <form
              className="mt-3 flex flex-wrap items-center gap-2"
              onSubmit={e => {
                e.preventDefault();
                void confirmar();
              }}
            >
              <input
                value={codigo}
                onChange={e => setCodigo(e.target.value)}
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                aria-label={tr('Código de la app', 'App code')}
                className="w-32 rounded-md border border-linea px-3 py-2 text-center text-sm tracking-[0.3em]"
              />
              <NotaDeAyuda titulo={tr('Código de la app', 'App code')} texto={A.codigo} />
              <button type="submit" disabled={activar.isPending || codigo.trim().length !== 6} className={boton}>
                {activar.isPending ? tr('Comprobando…', 'Checking…') : tr('Confirmar y activar', 'Confirm and turn on')}
              </button>
              {!obligatorios && (
                <button
                  type="button"
                  onClick={() => setPreparacion(null)}
                  className="text-sm text-apagado hover:text-tinta"
                >
                  {tr('Cancelar', 'Cancel')}
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      {usuario.dosPasosActivo && (
        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={e => {
            e.preventDefault();
            void quitar();
          }}
        >
          <span className="text-sm text-apagado">
            {tr('Para desactivarla, escribe un código vigente:', 'To turn it off, enter a current code:')}
          </span>
          <input
            value={codigo}
            onChange={e => setCodigo(e.target.value)}
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            aria-label={tr('Código de la app', 'App code')}
            className="w-32 rounded-md border border-linea px-3 py-2 text-center text-sm tracking-[0.3em]"
          />
          <NotaDeAyuda titulo={tr('Código de la app', 'App code')} texto={A.codigo} />
          <button
            type="submit"
            disabled={desactivar.isPending || codigo.trim().length !== 6}
            className="rounded-md border border-linea bg-white px-3 py-2 text-sm hover:bg-papel disabled:opacity-50"
          >
            {tr('Desactivar', 'Turn off')}
          </button>
        </form>
      )}
      {mensaje && <p className="mt-2 text-sm text-alerta">{mensaje}</p>}
      {!usuario.dosPasosActivo && !preparacion && !obligatorios && (
        <p className="mt-2 text-xs text-apagado">
          {tr(
            'Si pierdes el teléfono, un administrador puede restablecer tu contraseña y quitar la verificación desde',
            'If you lose your phone, an administrator can reset your password and remove the verification from',
          )}{' '}
          <Link to="/administracion/usuarios" className="text-verde hover:underline">
            {tr('Usuarios', 'Users')}
          </Link>
          .
        </p>
      )}
    </div>
  );
}
